import React, { useState, useEffect } from 'react';
import { X, Bell, Plus, Trash2, Edit, CheckCircle, AlertCircle } from 'lucide-react';
import { 
  Webhook, 
  getWebhooks, 
  registerWebhook, 
  updateWebhook, 
  deleteWebhook,
  availableWebhookEvents
} from '../lib/webhooks';

interface WebhookManagerProps {
  onClose: () => void;
}

export const WebhookManager: React.FC<WebhookManagerProps> = ({ onClose }) => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [isAddingWebhook, setIsAddingWebhook] = useState(false);
  const [isEditingWebhook, setIsEditingWebhook] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    url: '',
    events: ['*'],
    secret: ''
  });
  const [formError, setFormError] = useState<string | null>(null);
  const [formSuccess, setFormSuccess] = useState<string | null>(null);

  // Load webhooks
  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = () => {
    const hooks = getWebhooks();
    setWebhooks(hooks);
  };

  const handleAddWebhook = () => {
    setIsAddingWebhook(true);
    setIsEditingWebhook(null);
    setFormData({
      url: '',
      events: ['*'],
      secret: ''
    });
    setFormError(null);
    setFormSuccess(null);
  };

  const handleEditWebhook = (webhook: Webhook) => {
    setIsAddingWebhook(false);
    setIsEditingWebhook(webhook.id);
    setFormData({
      url: webhook.url,
      events: webhook.events,
      secret: webhook.secret || ''
    });
    setFormError(null);
    setFormSuccess(null);
  };

  const handleDeleteWebhook = (id: string) => {
    if (window.confirm('Möchten Sie diesen Webhook wirklich löschen?')) {
      try {
        deleteWebhook(id);
        loadWebhooks();
        setFormSuccess('Webhook erfolgreich gelöscht');
        setTimeout(() => setFormSuccess(null), 3000);
      } catch (error) {
        setFormError(error instanceof Error ? error.message : 'Fehler beim Löschen des Webhooks');
      }
    }
  };

  const handleToggleWebhook = (webhook: Webhook) => {
    try {
      updateWebhook(webhook.id, {
        active: !webhook.active
      });
      loadWebhooks();
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Fehler beim Aktualisieren des Webhooks');
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    setFormSuccess(null);

    try {
      if (isEditingWebhook) {
        // Update existing webhook
        updateWebhook(isEditingWebhook, {
          url: formData.url,
          events: formData.events,
          secret: formData.secret || undefined
        });
        setFormSuccess('Webhook erfolgreich aktualisiert');
      } else {
        // Create new webhook
        registerWebhook(
          formData.url,
          formData.events,
          formData.secret || undefined
        );
        setFormSuccess('Webhook erfolgreich erstellt');
      }

      loadWebhooks();
      setIsAddingWebhook(false);
      setIsEditingWebhook(null);
      
      // Clear success message after 3 seconds
      setTimeout(() => setFormSuccess(null), 3000);
    } catch (error) {
      setFormError(error instanceof Error ? error.message : 'Fehler beim Speichern des Webhooks');
    }
  };

  const handleCancel = () => {
    setIsAddingWebhook(false);
    setIsEditingWebhook(null);
    setFormError(null);
    setFormSuccess(null);
  };

  const handleEventChange = (event: string) => {
    if (event === '*') {
      // If selecting wildcard, clear all other events
      setFormData(prev => ({
        ...prev,
        events: ['*']
      }));
    } else {
      // If selecting a specific event, remove wildcard if present
      setFormData(prev => ({
        ...prev,
        events: prev.events.includes('*')
          ? [event]
          : prev.events.includes(event)
            ? prev.events.filter(e => e !== event)
            : [...prev.events, event]
      }));
    }
  };

  const formatDate = (date: Date | undefined) => {
    if (!date) return 'Nie';
    return date.toLocaleString('de-DE');
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Bell className="w-6 h-6 text-blue-600 shrink-0" />
            <span className="break-words">Webhooks verwalten</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors shrink-0"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Info box */}
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">Webhooks</h3>
            <p className="text-sm text-blue-700 mb-2">
              Webhooks ermöglichen es externen Anwendungen, Benachrichtigungen über Ereignisse in InstallTrack Pro zu erhalten.
            </p>
            <p className="text-sm text-blue-700">
              <strong>Hinweis:</strong> Die Ziel-URL muss öffentlich erreichbar sein und HTTP POST-Anfragen akzeptieren.
            </p>
          </div>

          {/* Success/Error messages */}
          {formSuccess && (
            <div className="bg-green-50 p-4 rounded-lg flex items-start">
              <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
              <p className="ml-3 text-sm text-green-700">{formSuccess}</p>
            </div>
          )}

          {formError && (
            <div className="bg-red-50 p-4 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
              <p className="ml-3 text-sm text-red-700">{formError}</p>
            </div>
          )}

          {/* Webhook form */}
          {(isAddingWebhook || isEditingWebhook) && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                {isEditingWebhook ? 'Webhook bearbeiten' : 'Neuen Webhook hinzufügen'}
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="url" className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook URL <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="url"
                    id="url"
                    value={formData.url}
                    onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                    className="w-full p-2.5 border border-gray-300 rounded-lg"
                    placeholder="https://example.com/webhook"
                    required
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Die URL, an die Webhook-Ereignisse gesendet werden.
                  </p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ereignisse <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="event-all"
                        checked={formData.events.includes('*')}
                        onChange={() => handleEventChange('*')}
                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="event-all" className="ml-2 text-sm text-gray-700">
                        Alle Ereignisse (*)
                      </label>
                    </div>
                    
                    {availableWebhookEvents.filter(event => event !== '*').map(event => (
                      <div key={event} className="flex items-center">
                        <input
                          type="checkbox"
                          id={`event-${event}`}
                          checked={formData.events.includes(event)}
                          onChange={() => handleEventChange(event)}
                          disabled={formData.events.includes('*')}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                        />
                        <label 
                          htmlFor={`event-${event}`} 
                          className={`ml-2 text-sm ${formData.events.includes('*') ? 'text-gray-400' : 'text-gray-700'}`}
                        >
                          {event}
                        </label>
                      </div>
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Die Ereignisse, für die dieser Webhook ausgelöst werden soll.
                  </p>
                </div>
                
                <div>
                  <label htmlFor="secret" className="block text-sm font-medium text-gray-700 mb-1">
                    Webhook Secret (optional)
                  </label>
                  <input
                    type="text"
                    id="secret"
                    value={formData.secret}
                    onChange={(e) => setFormData(prev => ({ ...prev, secret: e.target.value }))}
                    className="w-full p-2.5 border border-gray-300 rounded-lg"
                    placeholder="Ein geheimer Schlüssel für die Signaturvalidierung"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Ein geheimer Schlüssel, der zur Signierung von Webhook-Anfragen verwendet wird.
                  </p>
                </div>
                
                <div className="flex justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {isEditingWebhook ? 'Aktualisieren' : 'Erstellen'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Add webhook button */}
          {!isAddingWebhook && !isEditingWebhook && (
            <div className="flex justify-end">
              <button
                onClick={handleAddWebhook}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-4 h-4" />
                <span>Neuen Webhook hinzufügen</span>
              </button>
            </div>
          )}

          {/* Webhooks list */}
          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    URL
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ereignisse
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Zuletzt ausgelöst
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">Aktionen</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {webhooks.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                      Keine Webhooks vorhanden
                    </td>
                  </tr>
                ) : (
                  webhooks.map((webhook) => (
                    <tr key={webhook.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Bell className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900 break-all">
                              {webhook.url}
                            </div>
                            <div className="text-xs text-gray-500">
                              Erstellt am: {formatDate(webhook.createdAt)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {webhook.events.includes('*') ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              Alle Ereignisse
                            </span>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {webhook.events.map(event => (
                                <span 
                                  key={event}
                                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                >
                                  {event}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(webhook.lastTriggered)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleWebhook(webhook)}
                          className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                            webhook.active ? 'bg-blue-600' : 'bg-gray- 200'
                          }`}
                        >
                          <span className="sr-only">
                            {webhook.active ? 'Deaktivieren' : 'Aktivieren'}
                          </span>
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200 ${
                              webhook.active ? 'translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEditWebhook(webhook)}
                            className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                            title="Webhook bearbeiten"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteWebhook(webhook.id)}
                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50"
                            title="Webhook löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Schließen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};